import classNames from 'classnames';
import React from 'react';

import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {BlockList, IconButton} from '@udemy/react-core-components';
import {SideDrawer, SideDrawerProps} from '@udemy/react-dialog-components';

import {DemoFormHeaderLoginProps} from '../demo-form-header-login/demo-form-header-login.react-component';
import {DemoFormHeaderUFBLogo} from './demo-form-header-ufb-logo.react-component';
import {BaseDemoFormHeaderProps} from './demo-form-header.react-component';
import styles from './mobile-demo-form-header.module.less';

interface MobileDemoFormHeaderProps extends BaseDemoFormHeaderProps {
    sideDrawerProps: SideDrawerProps;
    loginProps: DemoFormHeaderLoginProps;
}

export const MobileDemoFormHeader = React.forwardRef<HTMLElement, MobileDemoFormHeaderProps>(
    (props, ref) => {
        const {logoButtonHref, onLogoButtonClick, sideDrawerProps, ...otherProps} = props;
        return (
            <div className={styles['header']} data-testid="mobile-header">
                <MobileDemoFormNav ref={ref} onToggle={sideDrawerProps.onToggle} {...otherProps} />
                <IconButton
                    udStyle="ghost"
                    cssToggleId="header-toggle-side-nav"
                    className={styles['ud-mobile-header-btn']}
                    data-purpose="side-menu-opener"
                >
                    <Icon glyph="menu" color="neutral" label={gettext('Open side drawer')} />
                </IconButton>
                <div className={classNames(styles['ufb-logo'])}>
                    <DemoFormHeaderUFBLogo
                        imgWidth={165}
                        imgHeight={28}
                        href={logoButtonHref}
                        onClick={onLogoButtonClick}
                        logoClassName={styles['ufb-logo']}
                    />
                </div>
            </div>
        );
    },
);

export interface MobileNavProps {
    onToggle: SideDrawerProps['onToggle'];
    loginProps: DemoFormHeaderLoginProps;
}

export const MobileDemoFormNav = React.forwardRef<HTMLElement, MobileNavProps>((props, ref) => {
    const {gettext} = useI18n();
    const {loginProps, ...otherProps} = props;
    return (
        <SideDrawer
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ref={ref as any}
            id="header-toggle-side-nav"
            mainDrawerId="header-toggle-side-nav-main"
            title={gettext('Login')}
            side="left"
            className={styles.nav}
            {...otherProps}
        >
            <BlockList size={'large'} className={styles['nav-item']}>
                <MobileNavLoginHeading />
                <MobileNavLoginOptions {...loginProps} />
            </BlockList>
        </SideDrawer>
    );
});

export const MobileNavLoginHeading = () => {
    return (
        <BlockList.Item className={styles['nav-item']}>
            <div className={styles['nav-section-heading']}>
                <h2 className="ud-text-md" data-testid="login-sidedrawer-heading">
                    {gettext('Login')}
                </h2>
            </div>
        </BlockList.Item>
    );
};

export const MobileNavLoginOptions = ({
    primaryUrl,
    secondaryUrl,
    secondaryUrlText,
    primaryUrlText,
    dropdownPrimaryOnClick,
    dropdownSecondaryOnClick,
}: DemoFormHeaderLoginProps) => {
    return (
        <div>
            <BlockList.Item>
                <div className={styles['login-button-text']}>
                    <a
                        className="ud-text-md"
                        href={primaryUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={dropdownPrimaryOnClick}
                    >
                        {primaryUrlText}
                    </a>
                </div>
            </BlockList.Item>
            <BlockList.Item>
                <div className={styles['login-button-text']}>
                    <a
                        className="ud-text-md"
                        href={secondaryUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={dropdownSecondaryOnClick}
                    >
                        {secondaryUrlText}
                    </a>
                </div>
            </BlockList.Item>
        </div>
    );
};
